import React, { useState, useEffect } from 'react';
import { 
  ExclamationTriangleIcon,
  ArrowTopRightOnSquareIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { 
  areExternalLinkWarningsDisabled,
  enableExternalLinkWarnings,
  disableExternalLinkWarnings,
  getPreferencesTimestamp
} from '../../../services/externalLinkPreferences';

const ExternalLinkPreferences = () => {
  const [warningsDisabled, setWarningsDisabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = () => {
    try {
      setLoading(true);
      const disabled = areExternalLinkWarningsDisabled();
      const timestamp = getPreferencesTimestamp();
      
      setWarningsDisabled(disabled);
      setLastUpdated(timestamp ? new Date(timestamp) : null);
    } catch (error) {
      console.error('Error loading external link preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleWarnings = async (disabled) => {
    try {
      setSaving(true);
      setSuccessMessage('');

      const success = disabled 
        ? disableExternalLinkWarnings()
        : enableExternalLinkWarnings();

      if (success) {
        setWarningsDisabled(disabled);
        setLastUpdated(new Date());
        setSuccessMessage(
          disabled 
            ? 'External link warnings have been disabled'
            : 'External link warnings have been enabled'
        );
        
        // Clear success message after 3 seconds
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        console.error('Failed to update external link preferences');
      }
    } catch (error) {
      console.error('Error updating external link preferences:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-4"></div>
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-2/3 mb-2"></div>
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
      {/* Header */}
      <div className="flex items-center space-x-3 mb-4">
        <div className="flex-shrink-0">
          <ArrowTopRightOnSquareIcon className="h-6 w-6 text-purple-600 dark:text-purple-400" />
        </div>
        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            External Link Warnings
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Control how external links are handled in forum posts and replies
          </p>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md">
          <div className="flex items-center">
            <CheckCircleIcon className="h-5 w-5 text-green-600 dark:text-green-400 mr-2" />
            <p className="text-sm text-green-700 dark:text-green-300">
              {successMessage}
            </p>
          </div>
        </div>
      )}

      {/* Current Status */}
      <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0 mt-0.5">
            {warningsDisabled ? (
              <div className="h-2 w-2 bg-gray-400 rounded-full"></div>
            ) : (
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
            )}
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              {warningsDisabled ? 'Warnings Disabled' : 'Warnings Enabled'}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {warningsDisabled 
                ? 'External links will open directly in new tabs without confirmation'
                : 'You will be prompted before navigating to external websites'
              }
            </p>
            {lastUpdated && (
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                Last updated: {lastUpdated.toLocaleDateString()} at {lastUpdated.toLocaleTimeString()}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Toggle Controls */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <label className="text-sm font-medium text-gray-900 dark:text-white">
              Show external link warnings
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              When enabled, you'll see a confirmation dialog before visiting external websites from forum content
            </p>
          </div>
          <div className="ml-4">
            <button
              type="button"
              disabled={saving}
              onClick={() => handleToggleWarnings(false)}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 ${
                !warningsDisabled
                  ? 'bg-purple-600'
                  : 'bg-gray-200 dark:bg-gray-600'
              } ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  !warningsDisabled ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="pt-4 border-t border-gray-200 dark:border-gray-600">
          <div className="flex space-x-3">
            {warningsDisabled && (
              <button
                type="button"
                disabled={saving}
                onClick={() => handleToggleWarnings(false)}
                className="inline-flex items-center px-3 py-2 border border-purple-300 dark:border-purple-600 shadow-sm text-sm leading-4 font-medium rounded-md text-purple-700 dark:text-purple-300 bg-white dark:bg-gray-700 hover:bg-purple-50 dark:hover:bg-purple-900/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 dark:focus:ring-offset-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ExclamationTriangleIcon className="h-4 w-4 mr-2" />
                {saving ? 'Enabling...' : 'Enable Warnings'}
              </button>
            )}
            
            {!warningsDisabled && (
              <button
                type="button"
                disabled={saving}
                onClick={() => handleToggleWarnings(true)}
                className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 dark:focus:ring-offset-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ArrowTopRightOnSquareIcon className="h-4 w-4 mr-2" />
                {saving ? 'Disabling...' : 'Disable Warnings'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Help Text */}
      <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
        <h4 className="text-sm font-medium text-blue-900 dark:text-blue-300 mb-2">
          About External Link Warnings
        </h4>
        <ul className="text-xs text-blue-700 dark:text-blue-400 space-y-1">
          <li>• External links always open in new tabs for security</li>
          <li>• Warnings help prevent accidental navigation away from discussions</li>
          <li>• You can temporarily disable warnings using the checkbox in the warning dialog</li>
          <li>• Internal links within the forum never show warnings</li>
        </ul>
      </div>
    </div>
  );
};

export default ExternalLinkPreferences;